/**
 * ChipPicker — chip-style option selector used by every Sun Stellar calc page.
 *
 * Visual spec mirrors `docs/calc-references/*.html` `.opt` chips: rounded
 * border, slate-300 default, slate-900 fill when active. The `yes-no` variant
 * swaps the active fill for emerald/rose, matching the reference's
 * green/red coding for boolean inputs (e.g. "Already have a tank?").
 *
 * Layout matches Task 3.2 §3.2 Step 1 of
 * `docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md`.
 */
import { ReactNode } from 'react';

export interface ChipOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface ChipPickerProps<T extends string> {
  legend: ReactNode;
  options: ReadonlyArray<ChipOption<T>>;
  value: T | null;
  onChange: (v: T) => void;
  variant?: 'default' | 'yes-no';
  className?: string;
}

export function ChipPicker<T extends string>({
  legend,
  options,
  value,
  onChange,
  variant = 'default',
  className = '',
}: ChipPickerProps<T>) {
  return (
    <fieldset className={`flex flex-col gap-2 ${className}`}>
      <legend className="text-sm font-medium text-slate-700">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = opt.value === value;
          const base =
            'px-4 py-2 rounded-lg border text-sm transition cursor-pointer select-none';
          const onClass =
            variant === 'yes-no'
              ? opt.value === 'yes'
                ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                : 'bg-rose-50 border-rose-400 text-rose-700'
              : 'bg-slate-900 text-white border-slate-900';
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`${base} ${
                active
                  ? onClass
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
